import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTokenStore, logout } from '../storage/tokenState';
import { NAVIGATION_ROUTES } from '../constant';

// Global API call tracker - tracks when API calls are made
// This prevents inactivity timer from resetting on API calls
let lastApiCallTime = 0;
let apiCallCount = 0;

/**
 * Track API call - call this before making API requests
 * This ensures inactivity timer doesn't reset on API calls
 */
export const trackApiCall = () => {
  lastApiCallTime = Date.now();
  apiCallCount++;
};

/**
 * Hook to automatically logout user when token expires or after inactivity
 * @param inactivityTimeout - Time in milliseconds for inactivity timeout (default: 5 minutes)
 * 
 * Features:
 * - Auto logout after 5 minutes of NO user interaction (mouse, keyboard, touch, scroll)
 * - Auto logout when token expires
 * - Auto logout when API returns 401
 * - Auto logout when user returns after tab was inactive for long time
 * - Does NOT reset timer on API calls
 * - Does NOT reset timer on focus/blur events
 */
export const useAutoLogout = (inactivityTimeout: number = 5 * 60 * 1000) => {
  const { token } = useTokenStore();
  const navigate = useNavigate();
  const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);
  const tokenCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const lastApiCallRef = useRef<number>(0);
  const isLoggingOutRef = useRef<boolean>(false);
  const backgroundTimeRef = useRef<number | null>(null);

  // CRITICAL: Check token validity with fresh data from store
  // This function always gets the latest token to avoid stale closure issues
  const checkTokenValidity = useCallback(() => {
    // Prevent multiple simultaneous logout calls
    if (isLoggingOutRef.current) {
      return false;
    }

    // ALWAYS get fresh token from store (critical for production)
    const currentToken = useTokenStore.getState().token;
    
    if (!currentToken?.accessToken) {
      return false; // No token, nothing to check
    }

    const currentTime = Date.now();
    const tokenValidity = currentToken?.validity || 0;
    const refreshTokenExpiresAt = currentToken?.refreshTokenExpiresAt || 0;
    const hasRefreshToken = !!(currentToken?.refreshToken);

    // Check if access token is expired
    const isAccessTokenExpired = tokenValidity > 0 && currentTime > tokenValidity;
    const isRefreshTokenExpired = refreshTokenExpiresAt > 0 && currentTime > refreshTokenExpiresAt;

    // If tokens are expired, logout immediately
    if (isAccessTokenExpired && (isRefreshTokenExpired || !hasRefreshToken)) {
      isLoggingOutRef.current = true;
      console.log('[AUTO-LOGOUT] Token expired, logging out...', {
        currentTime: new Date(currentTime).toISOString(),
        tokenValidity: tokenValidity > 0 ? new Date(tokenValidity).toISOString() : 'N/A',
        refreshTokenExpiresAt: refreshTokenExpiresAt > 0 ? new Date(refreshTokenExpiresAt).toISOString() : 'N/A',
        timeSinceExpiry: isAccessTokenExpired ? ((currentTime - tokenValidity) / 1000 / 60).toFixed(2) + ' minutes' : 'N/A'
      });
      logout();
      navigate(NAVIGATION_ROUTES.REFERRAL_HOME, { replace: true });
      return true; // Token expired, logged out
    }

    return false; // Token is valid
  }, [navigate]);

  // Check token validity and auto-logout if expired
  useEffect(() => {
    if (!token?.accessToken) {
      return;
    }

    const GRACE_PERIOD_AFTER_LOGIN = 15 * 1000; // 15 seconds grace period after login
    const tokenSetTime = Date.now();

    // CRITICAL: Immediate check for already-expired tokens (bypasses grace period)
    // This handles cases where user returns after hours with expired token
    if (checkTokenValidity()) {
      // Token was expired and user was logged out
      return;
    }

    // Regular token validity check (with grace period for fresh tokens)
    const performTokenCheck = () => {
      if (isLoggingOutRef.current) {
        return;
      }

      const currentTime = Date.now();
      const timeSinceTokenSet = currentTime - tokenSetTime;
      
      // Skip grace period check if token is already expired
      // Always check expired tokens immediately
      const currentToken = useTokenStore.getState().token;
      const tokenValidity = currentToken?.validity || 0;
      const isAlreadyExpired = tokenValidity > 0 && currentTime > tokenValidity;
      
      if (isAlreadyExpired) {
        checkTokenValidity();
        return;
      }

      // For non-expired tokens, respect grace period
      if (timeSinceTokenSet < GRACE_PERIOD_AFTER_LOGIN) {
        console.log('[AUTO-LOGOUT] Skipping check - within grace period (token is valid)', {
          timeSinceTokenSet: (timeSinceTokenSet / 1000).toFixed(2) + ' seconds',
          gracePeriod: (GRACE_PERIOD_AFTER_LOGIN / 1000) + ' seconds'
        });
        return;
      }

      // Perform regular check
      checkTokenValidity();
    };

    // Check token validity every 30 seconds for faster detection
    tokenCheckIntervalRef.current = setInterval(performTokenCheck, 30 * 1000);

    // Initial check after grace period
    const initialCheckTimeout = setTimeout(() => {
      performTokenCheck();
    }, GRACE_PERIOD_AFTER_LOGIN);

    return () => {
      clearTimeout(initialCheckTimeout);
      if (tokenCheckIntervalRef.current) {
        clearInterval(tokenCheckIntervalRef.current);
      }
    };
  }, [token, navigate, checkTokenValidity]);

  // Handle inactivity timeout - ONLY reset on user interaction, NOT on API calls or focus events
  useEffect(() => {
    if (!token?.accessToken) {
      return;
    }

    const resetInactivityTimer = () => {
      // Don't reset if we're already logging out
      if (isLoggingOutRef.current) {
        return;
      }

      // Update last activity time
      lastActivityRef.current = Date.now();
      
      // Clear existing timer
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }

      // Set new inactivity timer
      inactivityTimerRef.current = setTimeout(() => {
        if (isLoggingOutRef.current) {
          return;
        }
        isLoggingOutRef.current = true;
        console.log('[AUTO-LOGOUT] Inactivity timeout (' + (inactivityTimeout / 1000 / 60) + ' minutes), logging out...', {
          lastActivity: new Date(lastActivityRef.current).toISOString(),
          currentTime: new Date(Date.now()).toISOString(),
          inactiveFor: ((Date.now() - lastActivityRef.current) / 1000 / 60).toFixed(2) + ' minutes'
        });
        logout();
        navigate(NAVIGATION_ROUTES.REFERRAL_HOME, { replace: true });
      }, inactivityTimeout);
    };

    // Handle visibility change (when user switches tabs/apps)
    const handleVisibilityChange = () => {
      if (isLoggingOutRef.current) {
        return;
      }

      if (document.hidden) {
        // Page is hidden - store the time when it went to background
        // This is critical for mobile devices where timers may be throttled
        backgroundTimeRef.current = Date.now();
        console.log('[AUTO-LOGOUT] Page hidden, storing background time for mobile timer check');
      } else {
        // Page is visible again - check if user was inactive too long
        const currentTime = Date.now();
        const timeSinceLastActivity = currentTime - lastActivityRef.current;
        
        // Also check if we were in background for too long
        const backgroundDuration = backgroundTimeRef.current 
          ? currentTime - backgroundTimeRef.current 
          : 0;
        
        console.log('[AUTO-LOGOUT] Page visible, checking inactivity...', {
          timeSinceLastActivity: (timeSinceLastActivity / 1000 / 60).toFixed(2) + ' minutes',
          backgroundDuration: (backgroundDuration / 1000 / 60).toFixed(2) + ' minutes',
          inactivityTimeout: (inactivityTimeout / 1000 / 60) + ' minutes'
        });
        
        // Check if user was inactive for too long (either before or during background)
        if (timeSinceLastActivity >= inactivityTimeout) {
          // User was inactive for too long while away
          isLoggingOutRef.current = true;
          console.log('[AUTO-LOGOUT] User inactive for too long while away, logging out...', {
            timeSinceLastActivity: (timeSinceLastActivity / 1000 / 60).toFixed(2) + ' minutes'
          });
          logout();
          navigate(NAVIGATION_ROUTES.REFERRAL_HOME, { replace: true });
          return;
        }
        
        // Resume inactivity timer with remaining time
        const remainingTime = inactivityTimeout - timeSinceLastActivity;
        if (inactivityTimerRef.current) {
          clearTimeout(inactivityTimerRef.current);
        }
        inactivityTimerRef.current = setTimeout(() => {
          if (isLoggingOutRef.current) {
            return;
          }
          isLoggingOutRef.current = true;
          console.log('[AUTO-LOGOUT] Inactivity timeout after visibility change, logging out...');
          logout();
          navigate(NAVIGATION_ROUTES.REFERRAL_HOME, { replace: true });
        }, remainingTime);
        
        // Reset background time
        backgroundTimeRef.current = null;
        
        // Also check token validity when page becomes visible
        checkTokenValidity();
      }
    };

    // CRITICAL: Only reset timer on actual user interaction events
    // DO NOT reset on focus/blur events (user requirement)
    // DO NOT reset on API calls (tracked separately)
    const userInteractionEvents = ['mousedown', 'mousemove', 'keypress', 'keydown', 'scroll', 'touchstart', 'click', 'touchmove', 'wheel'];
    
    userInteractionEvents.forEach(event => {
      window.addEventListener(event, resetInactivityTimer, { passive: true });
    });

    // Listen for visibility changes (critical for mobile devices)
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start the timer
    resetInactivityTimer();

    // Periodic check to sync with API call tracking
    const apiSyncInterval = setInterval(() => {
      // Update last API call time from global tracker
      if (lastApiCallTime > lastApiCallRef.current) {
        lastApiCallRef.current = lastApiCallTime;
        // Don't reset inactivity timer on API calls
        console.log('[AUTO-LOGOUT] API call detected, NOT resetting inactivity timer', {
          apiCallCount,
          lastApiCall: new Date(lastApiCallTime).toISOString(),
          lastActivity: new Date(lastActivityRef.current).toISOString()
        });
      }
    }, 1000); // Check every second

    return () => {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
      if (apiSyncInterval) {
        clearInterval(apiSyncInterval);
      }
      userInteractionEvents.forEach(event => {
        window.removeEventListener(event, resetInactivityTimer);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, inactivityTimeout, navigate, checkTokenValidity]);
};

/**
 * Global function to handle 401 errors - call this from API interceptors
 * This will immediately logout the user
 */
export const handle401Error = () => {
  console.log('[AUTO-LOGOUT] 401 Unauthorized error detected, logging out immediately...');
  logout();
  // Use window.location for immediate redirect
  setTimeout(() => {
    window.location.href = NAVIGATION_ROUTES.REFERRAL_HOME;
  }, 100);
};
